/**
 * Concierge operational workflow — pure, side-effect-free policy layer.
 *
 * Separates the REQUEST state (operational progress) from the PAYMENT state
 * (money). Payment can only reach "pago" through a validated Stripe webhook or
 * an audited founder reconciliation — never through a UI dropdown.
 *
 * The database keeps its historical status vocabulary; this module derives the
 * richer operational vocabulary shown to staff and clients.
 */

export type RequestState =
  | "novo"
  | "em_analise"
  | "orcamento_enviado"
  | "aguarda_pagamento"
  | "pago"
  | "em_desenvolvimento"
  | "pronto"
  | "entregue"
  | "cancelado";

export type PaymentState =
  | "gratuito"
  | "checkout_por_emitir"
  | "checkout_enviado"
  | "em_processamento"
  | "pago"
  | "falhado"
  | "expirado"
  | "reembolsado"
  | "em_revisao";

/** Status values persisted in `concierge_requests.status`. */
export type DbStatus =
  | "novo"
  | "em_analise"
  | "em_desenvolvimento"
  | "concluido"
  | "entregue"
  | "cancelado";

export const REQUEST_STATE_LABEL: Record<RequestState, string> = {
  novo: "Novo",
  em_analise: "Em análise",
  orcamento_enviado: "Orçamento enviado",
  aguarda_pagamento: "A aguardar pagamento",
  pago: "Pago/confirmado",
  em_desenvolvimento: "Em desenvolvimento",
  pronto: "Pronto para entrega",
  entregue: "Entregue",
  cancelado: "Cancelado",
};

export const PAYMENT_STATE_LABEL: Record<PaymentState, string> = {
  gratuito: "Gratuito",
  checkout_por_emitir: "Checkout por emitir",
  checkout_enviado: "Checkout enviado",
  em_processamento: "Em processamento",
  pago: "Pago",
  falhado: "Falhado",
  expirado: "Expirado",
  reembolsado: "Reembolsado",
  em_revisao: "Em revisão",
};

export interface ConciergeSnapshot {
  status: string;
  paymentStatus: string;
  isFirstFree: boolean;
  priceCents: number | null;
  deliveredAt: string | null;
  hasDeliverable: boolean;
  activeQuote: { status: string; expiresAt: string | null } | null;
  quoteMismatch?: boolean;
}

function quoteExpired(expiresAt: string | null | undefined, now: Date): boolean {
  if (!expiresAt) return false;
  const t = Date.parse(expiresAt);
  return Number.isFinite(t) && t <= now.getTime();
}

export function derivePaymentState(s: ConciergeSnapshot, now: Date = new Date()): PaymentState {
  if (s.paymentStatus === "pago") return "pago";
  if (s.isFirstFree || s.paymentStatus === "nao_aplicavel") return "gratuito";
  if (s.paymentStatus === "reembolsado") return "reembolsado";
  if (s.quoteMismatch || s.paymentStatus === "em_revisao") return "em_revisao";
  if (s.paymentStatus === "falhado") return "falhado";
  if (s.paymentStatus === "em_processamento") return "em_processamento";

  const q = s.activeQuote;
  if (q?.status === "checkout_created") {
    return quoteExpired(q.expiresAt, now) ? "expirado" : "checkout_enviado";
  }
  if (q?.status === "quoted") return "checkout_por_emitir";
  if (s.paymentStatus === "pendente") return "checkout_enviado";
  return "checkout_por_emitir";
}

export function deriveRequestState(s: ConciergeSnapshot, now: Date = new Date()): RequestState {
  if (s.status === "cancelado") return "cancelado";
  if (s.deliveredAt || s.status === "entregue") return "entregue";
  if (s.status === "concluido") return "pronto";
  if (s.status === "em_desenvolvimento") return "em_desenvolvimento";

  const pay = derivePaymentState(s, now);
  if (pay === "pago") return "pago";
  if (pay === "checkout_enviado" || pay === "expirado" || pay === "em_processamento")
    return "aguarda_pagamento";
  if (pay === "checkout_por_emitir" && s.activeQuote?.status === "quoted")
    return "orcamento_enviado";
  return s.status === "em_analise" ? "em_analise" : "novo";
}

// ─── Ações ────────────────────────────────────────────────────────────────

export type ConciergeAction =
  | "save_quote"
  | "send_payment_link"
  | "resend_payment_link"
  | "replace_quote"
  | "start_development"
  | "mark_ready"
  | "deliver"
  | "cancel"
  | "reconcile_payment";

export const ACTION_LABEL: Record<ConciergeAction, string> = {
  save_quote: "Guardar orçamento",
  send_payment_link: "Enviar link de pagamento",
  resend_payment_link: "Reenviar link",
  replace_quote: "Substituir orçamento",
  start_development: "Iniciar desenvolvimento",
  mark_ready: "Marcar como pronto",
  deliver: "Entregar ao cliente",
  cancel: "Cancelar",
  reconcile_payment: "Reconciliar pagamento",
};

export function availableActions(
  s: ConciergeSnapshot,
  opts: { isFounder?: boolean; now?: Date } = {},
): ConciergeAction[] {
  const now = opts.now ?? new Date();
  const request = deriveRequestState(s, now);
  const payment = derivePaymentState(s, now);
  const actions: ConciergeAction[] = [];
  const closed = request === "entregue" || request === "cancelado";
  const settled = payment === "pago" || payment === "reembolsado";
  const free = payment === "gratuito";

  if (!closed && !settled) actions.push("save_quote");

  if (!closed && !settled && !free && (s.priceCents ?? 0) >= 100) {
    if (s.activeQuote) {
      actions.push("resend_payment_link", "replace_quote");
    } else {
      actions.push("send_payment_link");
    }
  }

  if (!closed && s.status !== "em_desenvolvimento" && s.status !== "concluido") {
    if (payment === "pago" || free) actions.push("start_development");
  }
  if (!closed && s.status === "em_desenvolvimento") actions.push("mark_ready");
  if (
    !closed &&
    s.status === "concluido" &&
    s.hasDeliverable &&
    (payment === "pago" || free)
  )
    actions.push("deliver");
  if (!closed) actions.push("cancel");
  if (opts.isFounder && !free && payment !== "pago" && request !== "cancelado")
    actions.push("reconcile_payment");

  return actions;
}

// ─── Transições ───────────────────────────────────────────────────────────

export const ALLOWED_STATUS_TRANSITIONS: Record<DbStatus, DbStatus[]> = {
  novo: ["em_analise", "cancelado"],
  em_analise: ["em_desenvolvimento", "cancelado", "novo"],
  em_desenvolvimento: ["concluido", "em_analise", "cancelado"],
  concluido: ["entregue", "em_desenvolvimento", "cancelado"],
  entregue: [],
  cancelado: ["novo"],
};

export type TransitionCheck = { ok: true } | { ok: false; reason: string };

export function checkStatusTransition(
  from: string,
  to: string,
  ctx: {
    paymentState: PaymentState;
    manual?: boolean;
    override?: boolean;
    hasDeliverable?: boolean;
  },
): TransitionCheck {
  if (from === to) return { ok: true };
  const allowed = ALLOWED_STATUS_TRANSITIONS[from as DbStatus] ?? [];
  if (!allowed.includes(to as DbStatus))
    return { ok: false, reason: `Transição de estado inválida: ${from} → ${to}` };

  if (to === "entregue" && ctx.manual !== false)
    return {
      ok: false,
      reason: 'Usa a ação "Entregar ao cliente" — o estado Entregue não é editável manualmente.',
    };

  if (to === "em_desenvolvimento") {
    const paid = ctx.paymentState === "pago" || ctx.paymentState === "gratuito";
    if (!paid && !ctx.override)
      return {
        ok: false,
        reason:
          "Não podes iniciar o desenvolvimento antes do pagamento (usa um override auditado do fundador).",
      };
  }
  return { ok: true };
}

export function checkDeliverable(s: ConciergeSnapshot, now: Date = new Date()): TransitionCheck {
  if (s.deliveredAt) return { ok: false, reason: "already_delivered" };
  if (s.status !== "concluido")
    return { ok: false, reason: 'Marca o pedido como "Pronto para entrega" antes de entregar.' };
  if (!s.hasDeliverable)
    return { ok: false, reason: "Sem conteúdo para entregar: preenche texto, PDF ou link." };
  const pay = derivePaymentState(s, now);
  if (pay !== "pago" && pay !== "gratuito")
    return { ok: false, reason: "Não podes entregar antes de o cliente pagar." };
  return { ok: true };
}

export function canEditQuote(s: ConciergeSnapshot, now: Date = new Date()): TransitionCheck {
  const pay = derivePaymentState(s, now);
  if (pay === "pago") return { ok: false, reason: "Um orçamento pago não pode ser alterado." };
  if (s.deliveredAt) return { ok: false, reason: "O pedido já foi entregue." };
  return { ok: true };
}

// ─── Timeline do cliente ──────────────────────────────────────────────────

export type TimelineStepId =
  | "recebido"
  | "em_analise"
  | "orcamento"
  | "pagamento_pendente"
  | "pagamento_confirmado"
  | "em_preparacao"
  | "pronto"
  | "entregue";

export interface TimelineStep {
  id: TimelineStepId;
  label: string;
  state: "done" | "current" | "pending";
}

const TIMELINE: Array<{ id: TimelineStepId; label: string }> = [
  { id: "recebido", label: "Pedido recebido" },
  { id: "em_analise", label: "Em análise" },
  { id: "orcamento", label: "Orçamento disponível" },
  { id: "pagamento_pendente", label: "Pagamento pendente" },
  { id: "pagamento_confirmado", label: "Pagamento confirmado" },
  { id: "em_preparacao", label: "Em preparação" },
  { id: "pronto", label: "Pronto" },
  { id: "entregue", label: "Entregue" },
];

const STATE_INDEX: Record<RequestState, number> = {
  novo: 0,
  em_analise: 1,
  orcamento_enviado: 2,
  aguarda_pagamento: 3,
  pago: 4,
  em_desenvolvimento: 5,
  pronto: 6,
  entregue: 7,
  cancelado: 0,
};

export function conciergeTimeline(s: ConciergeSnapshot, now: Date = new Date()): TimelineStep[] {
  const request = deriveRequestState(s, now);
  const payment = derivePaymentState(s, now);
  const current = STATE_INDEX[request];
  const free = payment === "gratuito";
  return TIMELINE.filter(
    (step) =>
      !free || (step.id !== "orcamento" && step.id !== "pagamento_pendente"),
  ).map((step) => {
    const idx = TIMELINE.findIndex((t) => t.id === step.id);
    return {
      ...step,
      state: idx < current ? "done" : idx === current ? "current" : "pending",
    };
  });
}

/** Mostra o botão de pagamento apenas para o orçamento ativo ainda não pago. */
export function showPaymentButton(s: ConciergeSnapshot, now: Date = new Date()): boolean {
  const pay = derivePaymentState(s, now);
  return (
    pay === "checkout_enviado" &&
    s.activeQuote?.status === "checkout_created" &&
    !s.deliveredAt
  );
}

// ─── Emails idempotentes ──────────────────────────────────────────────────

export type ConciergeEmailEvent =
  | "submitted"
  | "under_review"
  | "quote_sent"
  | "payment_confirmed"
  | "founder_payment_alert"
  | "ready"
  | "delivered"
  | "cancelled"
  | "refunded";

export function conciergeEmailKey(
  event: ConciergeEmailEvent,
  requestId: string,
  version?: number | null,
): string {
  return `concierge-${event}-${requestId}${version != null ? `-v${version}` : ""}`;
}

export type SendOnceResult = { status: "sent" | "skipped" | "failed"; reason?: string };

/**
 * Envia um email exatamente uma vez por (pedido, versão, evento).
 * `hasSent` consulta o histórico; falhas ficam disponíveis para retry seguro.
 */
export async function sendConciergeEmailOnce(opts: {
  key: string;
  hasSent: (key: string) => Promise<boolean>;
  send: (key: string) => Promise<void>;
}): Promise<SendOnceResult> {
  if (await opts.hasSent(opts.key)) return { status: "skipped", reason: "already_sent" };
  try {
    await opts.send(opts.key);
    return { status: "sent" };
  } catch (e) {
    return { status: "failed", reason: e instanceof Error ? e.message : "unknown" };
  }
}

// ─── Public-facing localization ────────────────────────────────────────────
// "Concierge" is an internal identifier only. Everything shown to clients
// (status labels, actions, timeline) is localized from the active language.
// See src/lib/i18n.ts for the localized service name itself.

export type ConciergeUiLang = "pt" | "en" | "es" | "fr" | "it" | "de";

function resolveUiLang(lang: string | null | undefined): ConciergeUiLang {
  const l = (lang ?? "pt").toLowerCase();
  return (["pt", "en", "es", "fr", "it", "de"] as const).includes(l as ConciergeUiLang)
    ? (l as ConciergeUiLang)
    : "en";
}

export const REQUEST_STATE_LABEL_I18N: Record<ConciergeUiLang, Record<RequestState, string>> = {
  pt: REQUEST_STATE_LABEL,
  en: {
    novo: "New",
    em_analise: "Under review",
    orcamento_enviado: "Quote sent",
    aguarda_pagamento: "Awaiting payment",
    pago: "Paid/confirmed",
    em_desenvolvimento: "In progress",
    pronto: "Ready for delivery",
    entregue: "Delivered",
    cancelado: "Cancelled",
  },
  es: {
    novo: "Nuevo",
    em_analise: "En análisis",
    orcamento_enviado: "Presupuesto enviado",
    aguarda_pagamento: "Esperando pago",
    pago: "Pagado/confirmado",
    em_desenvolvimento: "En desarrollo",
    pronto: "Listo para entrega",
    entregue: "Entregado",
    cancelado: "Cancelado",
  },
  fr: {
    novo: "Nouveau",
    em_analise: "En cours d'analyse",
    orcamento_enviado: "Devis envoyé",
    aguarda_pagamento: "En attente de paiement",
    pago: "Payé/confirmé",
    em_desenvolvimento: "En développement",
    pronto: "Prêt à livrer",
    entregue: "Livré",
    cancelado: "Annulé",
  },
  it: {
    novo: "Nuovo",
    em_analise: "In fase di analisi",
    orcamento_enviado: "Preventivo inviato",
    aguarda_pagamento: "In attesa di pagamento",
    pago: "Pagato/confermato",
    em_desenvolvimento: "In lavorazione",
    pronto: "Pronto per la consegna",
    entregue: "Consegnato",
    cancelado: "Annullato",
  },
  de: {
    novo: "Neu",
    em_analise: "In Prüfung",
    orcamento_enviado: "Angebot gesendet",
    aguarda_pagamento: "Zahlung ausstehend",
    pago: "Bezahlt/bestätigt",
    em_desenvolvimento: "In Bearbeitung",
    pronto: "Bereit zur Lieferung",
    entregue: "Geliefert",
    cancelado: "Storniert",
  },
};

export const PAYMENT_STATE_LABEL_I18N: Record<ConciergeUiLang, Record<PaymentState, string>> = {
  pt: PAYMENT_STATE_LABEL,
  en: {
    gratuito: "Free",
    checkout_por_emitir: "Checkout to be issued",
    checkout_enviado: "Checkout sent",
    em_processamento: "Processing",
    pago: "Paid",
    falhado: "Failed",
    expirado: "Expired",
    reembolsado: "Refunded",
    em_revisao: "Under review",
  },
  es: {
    gratuito: "Gratuito",
    checkout_por_emitir: "Pago por emitir",
    checkout_enviado: "Pago enviado",
    em_processamento: "En proceso",
    pago: "Pagado",
    falhado: "Fallido",
    expirado: "Caducado",
    reembolsado: "Reembolsado",
    em_revisao: "En revisión",
  },
  fr: {
    gratuito: "Gratuit",
    checkout_por_emitir: "Paiement à émettre",
    checkout_enviado: "Paiement envoyé",
    em_processamento: "En traitement",
    pago: "Payé",
    falhado: "Échoué",
    expirado: "Expiré",
    reembolsado: "Remboursé",
    em_revisao: "En révision",
  },
  it: {
    gratuito: "Gratuito",
    checkout_por_emitir: "Pagamento da emettere",
    checkout_enviado: "Pagamento inviato",
    em_processamento: "In elaborazione",
    pago: "Pagato",
    falhado: "Fallito",
    expirado: "Scaduto",
    reembolsado: "Rimborsato",
    em_revisao: "In revisione",
  },
  de: {
    gratuito: "Kostenlos",
    checkout_por_emitir: "Zahlung ausstehend",
    checkout_enviado: "Zahlungslink gesendet",
    em_processamento: "In Bearbeitung",
    pago: "Bezahlt",
    falhado: "Fehlgeschlagen",
    expirado: "Abgelaufen",
    reembolsado: "Erstattet",
    em_revisao: "In Überprüfung",
  },
};

const TIMELINE_LABEL_I18N: Record<ConciergeUiLang, Record<TimelineStepId, string>> = {
  pt: {
    recebido: "Pedido recebido",
    em_analise: "Em análise",
    orcamento: "Orçamento disponível",
    pagamento_pendente: "Pagamento pendente",
    pagamento_confirmado: "Pagamento confirmado",
    em_preparacao: "Em preparação",
    pronto: "Pronto",
    entregue: "Entregue",
  },
  en: {
    recebido: "Request received",
    em_analise: "Under review",
    orcamento: "Quote available",
    pagamento_pendente: "Payment pending",
    pagamento_confirmado: "Payment confirmed",
    em_preparacao: "In preparation",
    pronto: "Ready",
    entregue: "Delivered",
  },
  es: {
    recebido: "Pedido recibido",
    em_analise: "En análisis",
    orcamento: "Presupuesto disponible",
    pagamento_pendente: "Pago pendiente",
    pagamento_confirmado: "Pago confirmado",
    em_preparacao: "En preparación",
    pronto: "Listo",
    entregue: "Entregado",
  },
  fr: {
    recebido: "Demande reçue",
    em_analise: "En cours d'analyse",
    orcamento: "Devis disponible",
    pagamento_pendente: "Paiement en attente",
    pagamento_confirmado: "Paiement confirmé",
    em_preparacao: "En préparation",
    pronto: "Prêt",
    entregue: "Livré",
  },
  it: {
    recebido: "Richiesta ricevuta",
    em_analise: "In fase di analisi",
    orcamento: "Preventivo disponibile",
    pagamento_pendente: "Pagamento in sospeso",
    pagamento_confirmado: "Pagamento confermato",
    em_preparacao: "In preparazione",
    pronto: "Pronto",
    entregue: "Consegnato",
  },
  de: {
    recebido: "Anfrage erhalten",
    em_analise: "In Prüfung",
    orcamento: "Angebot verfügbar",
    pagamento_pendente: "Zahlung ausstehend",
    pagamento_confirmado: "Zahlung bestätigt",
    em_preparacao: "In Vorbereitung",
    pronto: "Bereit",
    entregue: "Geliefert",
  },
};

/** Localized version of `conciergeTimeline()` for public-facing UI. */
export function conciergeTimelineLocalized(
  s: ConciergeSnapshot,
  lang: string | null | undefined,
  now: Date = new Date(),
): TimelineStep[] {
  const uiLang = resolveUiLang(lang);
  const labels = TIMELINE_LABEL_I18N[uiLang];
  return conciergeTimeline(s, now).map((step) => ({ ...step, label: labels[step.id] }));
}

export function getRequestStateLabel(state: RequestState, lang: string | null | undefined): string {
  return REQUEST_STATE_LABEL_I18N[resolveUiLang(lang)][state];
}

export function getPaymentStateLabel(state: PaymentState, lang: string | null | undefined): string {
  return PAYMENT_STATE_LABEL_I18N[resolveUiLang(lang)][state];
}
